import * as v from 'valibot';

import { DynamicData, TimeType } from '../common.types';

const AllNoneSome = v.picklist([1, 2, 3]);

const DaysOfWeek = v.picklist([1, 2, 3, 4, 5, 6, 7]);

const OpeningClosingTime = v.object({
	dayOfWeek: DaysOfWeek,
	openingTime: v.nullable(v.optional(v.string())),
	closingTime: v.nullable(v.optional(v.string())),
	isTwentyFourHours: v.boolean(),
});

const SeasonalityPeriod = v.object({
	fromDate: v.string(), // YYYY-MM-DD
	toDate: v.string(), // YYYY-MM-DD
	reason: v.string(),
	comment: v.string(),
});

const BusinessClosedPeriod = v.object({
	fromDate: v.string(), // YYYY-MM-DD
	toDate: v.string(), // YYYY-MM-DD
	reason: v.string(),
});

export type SeasonalityPeriod = v.InferOutput<typeof SeasonalityPeriod>;
export type BusinessClosedPeriod = v.InferOutput<typeof BusinessClosedPeriod>;

const QuestionnaireData = v.partial(
	v.object({
		ein: v.nullish(v.string()),
		businessOperationDescription: v.nullish(v.string()),
		// operationDays: v.nullish(v.array(v.picklist([0, 1, 2, 3, 4, 5, 6]))),
		// allDay: v.nullish(v.boolean()),
		// openingTime: v.nullish(v.string()),
		// closingTime: v.nullish(v.string()),
		businessOperationLessThan12Months: v.nullish(v.boolean()),
		businessOperationAsOfOIncidentDateTimeType: v.nullish(TimeType),
		businessOperationAsOfOIncidentNumber: v.nullish(v.number()),
		isBusinessEndOfYearFiscal: v.nullish(v.boolean()),
		fiscalMonth: v.nullish(v.number()),
		majorChangesToBusinessPriorToLoss: v.nullish(v.string()),
		// businessClosedDays: v.nullish(v.string()),
		// businessSeasonality: v.nullish(v.string()),
		customersCreditCardsAccepted: v.nullish(v.boolean()),
		generalImpactDescription: v.nullish(v.string()),
		incidentTime: v.nullish(v.string()),
		operationsShutDown: v.nullish(v.boolean()),
		rebuildNeeded: v.nullish(v.boolean()),

		managerPayrollContinued: v.nullish(AllNoneSome),
		managerContinuingWagePercent: v.nullish(v.number()),
		nonManagerPayrollContinued: v.nullish(AllNoneSome),
		nonManagerContinuingWagePercent: v.nullish(v.number()),
		ownersPayrollOnFinancialStatement: v.nullish(v.boolean()),
		ownersCompensationIncludedOnFinancialStatements: v.nullish(AllNoneSome),
		ownersPayrollOnFinancialStatementPercent: v.nullish(v.number()),

		employeeTippedWages: v.nullish(v.boolean()),

		naicsCodeId: v.nullish(v.number()),

		openingClosingTimes: v.nullish(v.array(OpeningClosingTime)),
		businessSeasonality: v.nullish(v.array(SeasonalityPeriod)),
		businessClosedDays: v.nullish(v.array(BusinessClosedPeriod)),
	}),
);

const NotStarted = v.object({
	isTier2: v.boolean(),
	status: v.literal('NotStarted'),
});

const Draft = v.object({
	status: v.literal('Draft'),
	isTier2: v.boolean(),
	questionnaireDetails: v.object({
		claimId: v.number(),
		...QuestionnaireData.entries,
		submitted: v.boolean(),
	}),
});

const Completed = v.object({
	status: v.literal('Completed'),
	isTier2: v.boolean(),
	questionnaireDetails: v.object({
		claimId: v.number(),
		...QuestionnaireData.entries,
		submitted: v.boolean(),
	}),
	questionnaireViewData: DynamicData,
});

export const Questionnaire = v.variant('status', [
	NotStarted,
	Draft,
	Completed,
]);

export type Questionnaire = v.InferOutput<typeof Questionnaire>;

export type QuestionnaireDetails = v.InferOutput<typeof QuestionnaireData>;

export const QuestionnaireBody = v.object({
	isDraft: v.boolean(),
	questionnaireData: v.object({
		claimId: v.number(),
		...QuestionnaireData.entries,
	}),
});

export type QuestionnaireBody = v.InferOutput<typeof QuestionnaireBody>;

export const QuestionnaireResponse = v.object({
	errors: v.optional(
		v.array(
			v.object({
				propertyName: v.keyof(QuestionnaireData),
				errorMessage: v.string(),
			}),
		),
	),
});

export type QuestionnaireResponse = v.InferOutput<typeof QuestionnaireResponse>;
